import React, { useEffect, useState } from "react";
import { custApi } from "../../lib/customerApi";
import { toast } from "sonner";
import {
  Coins, Wallet, Users, Receipt, Copy, ShareNetwork, Trophy, ArrowsClockwise,
} from "@phosphor-icons/react";

export default function MeAffiliate() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try { setData((await custApi.get("/me/affiliate")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  // Iter 138 — Canonical rewrite. If the backend returns a URL that
  // still points at the preview / staging host (e.g. because prod
  // env has an outdated PUBLIC_APP_URL), we rewrite it to the
  // customer-facing domain here as a last line of defense.
  const CANON = "https://www.rescigo.in";
  const BAD_HOSTS = ["rescigo-master-panel", "emergent.host",
                       "preview.emergentagent.com", "localhost",
                       "127.0.0.1"];
  const absUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("/")) return `${CANON}${u}`;
    try {
      const p = new URL(u);
      if (BAD_HOSTS.some(h => p.hostname.includes(h))) {
        return `${CANON}${p.pathname}${p.search}${p.hash}`;
      }
      return u;
    } catch { return u; }
  };

  const copyLink = async () => {
    if (!data?.referral_link) return;
    const url = absUrl(data.referral_link);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Referral link copied");
    } catch { toast.success(url); }
  };
  const shareLink = async () => {
    if (!data?.referral_link) return;
    const url = absUrl(data.referral_link);
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Rescigo Health",
          text: `${data.customer.name} invited you to Rescigo Health waste-management. Sign up via my link & we both get rewarded.`,
          url,
        });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  if (!data) return <div className="text-sm text-[#5C5A55] py-10 text-center" data-testid="ma-loading">Loading…</div>;
  const w = data.wallet;
  const link = absUrl(data.referral_link);

  return (
    <div className="space-y-5 px-4 py-4" data-testid="me-affiliate">
      {/* Hero — wallet */}
      <div className="bg-gradient-to-br from-[#DC2626] to-[#3F7A37] rounded-xl p-5 text-white shadow-sm">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] opacity-80">
          <Wallet size={12} weight="fill"/> My Reward Wallet
        </div>
        <div className="mt-2 text-4xl font-light tabular-nums" data-testid="ma-available">₹{Number(w.available || 0).toFixed(2)}</div>
        <div className="text-xs opacity-80 mt-1">Available balance · Min payout ₹{data.payout_min}</div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
          <div className="bg-white/10 rounded-md p-2.5">
            <div className="opacity-70 text-[10px] uppercase tracking-wider">Earned</div>
            <div className="text-base mt-0.5 tabular-nums">₹{Number(w.total_earned || 0).toFixed(2)}</div>
          </div>
          <div className="bg-white/10 rounded-md p-2.5">
            <div className="opacity-70 text-[10px] uppercase tracking-wider">Paid out</div>
            <div className="text-base mt-0.5 tabular-nums">₹{Number(w.total_paid || 0).toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Referral link card */}
      <div className="bg-white border border-[#E5E2D9] rounded-xl p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
          <ShareNetwork size={12} weight="fill"/> Your referral link
        </div>
        <div className="mt-2 font-mono text-xs text-[#1A1A1A] break-all bg-[#F7F6F2] rounded-md p-2.5 border border-[#E5E2D9]"
             data-testid="ma-link">
          {link || "—"}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={copyLink} data-testid="ma-copy"
                  className="h-10 inline-flex items-center justify-center gap-1.5 rounded-md border border-[#DC2626] text-[#DC2626] text-sm font-medium hover:bg-[#EFF3EE]">
            <Copy size={14}/> Copy
          </button>
          <button onClick={shareLink} data-testid="ma-share"
                  className="h-10 inline-flex items-center justify-center gap-1.5 rounded-md bg-[#DC2626] text-white text-sm font-medium hover:bg-[#B91C1C]">
            <ShareNetwork size={14}/> Share
          </button>
        </div>
        <div className="mt-3 text-[11px] text-[#8A8780] leading-relaxed">
          Share this link. You'll earn <span className="font-medium text-[#DC2626]">{(data.reward_pct*100).toFixed(0)}%</span>
          {" "}cash-back when a referred customer pays for their first package. Payouts are processed manually by our team once your balance crosses ₹{data.payout_min}.
        </div>
      </div>

      {/* Tab nav */}
      <div className="flex border border-[#E5E2D9] rounded-md overflow-hidden bg-white" data-testid="ma-tabs">
        {[
          {k:"overview",l:"Referred",i:Users},
          {k:"rewards",l:"Rewards",i:Trophy},
          {k:"payouts",l:"Payouts",i:Coins},
          {k:"ledger",l:"Ledger",i:Receipt},
        ].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)} data-testid={`ma-tab-${t.k}`}
                  className={`flex-1 h-10 text-[11px] uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition-colors ${tab===t.k?"bg-[#DC2626] text-white":"text-[#5C5A55] hover:bg-[#F7F6F2]"}`}>
            <t.i size={12} weight={tab===t.k?"fill":"regular"}/>{t.l}
          </button>
        ))}
      </div>

      {/* Tab body */}
      {tab === "overview" && (
        <List title={`Referred customers (${data.referred_customers.length})`} empty="No referrals yet — share your link above.">
          {data.referred_customers.map(c=>(
            <Row key={c.id} primary={c.name}
                  secondary={c.customer_code}
                  badge={c.status}
                  meta={c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}/>
          ))}
        </List>
      )}
      {tab === "rewards" && (
        <List title={`Reward credits (${data.referrals.length})`} empty="No rewards credited yet.">
          {data.referrals.map(r=>(
            <Row key={r.id}
                  primary={`+ ₹${Number(r.reward_amount).toFixed(2)}`}
                  primaryClass="text-[#DC2626] font-semibold"
                  secondary={`${r.referred_customer_name} · ${r.invoice_no}`}
                  meta={r.rewarded_at ? new Date(r.rewarded_at).toLocaleDateString() : ""}/>
          ))}
        </List>
      )}
      {tab === "payouts" && (
        <List title={`Payouts (${data.payouts.length})`} empty="No payouts yet.">
          {data.payouts.map(p=>(
            <Row key={p.id}
                  primary={`₹${Number(p.amount).toFixed(2)} · ${p.method?.toUpperCase()}`}
                  secondary={p.upi_txn_id ? `UTR ${p.upi_txn_id}` : (p.notes || "—")}
                  meta={p.paid_date}/>
          ))}
        </List>
      )}
      {tab === "ledger" && (
        <List title={`Wallet ledger (${data.ledger.length})`} empty="No transactions.">
          {data.ledger.map(l=>(
            <Row key={l.id}
                  primary={`${l.type === "credit" ? "+" : "−"} ₹${Number(l.amount).toFixed(2)}`}
                  primaryClass={l.type === "credit" ? "text-[#DC2626] font-semibold" : "text-[#7A2E18] font-semibold"}
                  secondary={l.source.replace(/_/g," ")}
                  meta={l.ts ? new Date(l.ts).toLocaleDateString() : ""}/>
          ))}
        </List>
      )}

      <button onClick={load} data-testid="ma-refresh"
              className="w-full h-10 inline-flex items-center justify-center gap-2 text-xs text-[#5C5A55] hover:bg-[#F7F6F2] rounded-md">
        <ArrowsClockwise size={12}/> Refresh
      </button>
    </div>
  );
}

const List = ({ title, empty, children }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
    <div className="px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] border-b border-[#E5E2D9]">
      {title}
    </div>
    {React.Children.count(children) === 0 ? (
      <div className="p-8 text-center text-sm text-[#8A8780]">{empty}</div>
    ) : children}
  </div>
);

const Row = ({ primary, primaryClass, secondary, meta, badge }) => (
  <div className="flex items-center gap-3 px-4 py-3 border-b border-[#F0ECE3] last:border-0">
    <div className="flex-1 min-w-0">
      <div className={`text-sm truncate ${primaryClass || "text-[#1A1A1A]"}`}>{primary}</div>
      {secondary && <div className="text-[11px] text-[#8A8780] truncate font-mono">{secondary}</div>}
    </div>
    {badge && (
      <span className={`text-[9px] px-2 py-0.5 rounded uppercase tracking-wider ${
        badge === "active" || badge === "approved" ? "bg-[#FEE2E2] text-[#7F1D1D]" :
        "bg-[#F9E4B7] text-[#7A5A11]"
      }`}>{badge}</span>
    )}
    {meta && <div className="text-[10px] text-[#8A8780] tabular-nums">{meta}</div>}
  </div>
);
